// src/components/products/ProductReviewActions.tsx
import React, { useState, version } from 'react';
import { CheckCircle, XCircle } from 'lucide-react';
import { adminAPI } from '../../api/admin';
import RejectModal from './RejectModal';
import ConfirmationModal from '../common/ConfirmationModal';
import toast from '../../utils/toast';

interface ProductReviewActionsProps {
    productId: number;
    versionId?: number | null;
    productTitle: string;
    vendorName: string;
    onSuccess: () => void;
    onClose: () => void;
    hasPreviousVersion?: boolean;
}

const ProductReviewActions: React.FC<ProductReviewActionsProps> = ({
    productId,
    versionId,
    productTitle,
    vendorName,
    onSuccess,
    onClose,
}) => {
    const [processing, setProcessing] = useState(false);
    const [showRejectModal, setShowRejectModal] = useState(false);
    const [showApproveModal, setShowApproveModal] = useState(false);
    const hasPreviousVersion = !!versionId;
    const handleReviewAction = async (action: 'approve' | 'reject' | 'reject_changes' | 'request_changes', reason?: string) => {
        setProcessing(true);
        try {
            const params: any = { action };
            
            // if (productId)
                params.productId = productId;
            if (versionId)
                params.versionId = versionId;



            if (reason) 
                params.reason = reason;
            

            const response = await adminAPI.reviewVersion(params);

            if (response.error) {
                toast.error(response.error);
            } else {
                const messages = {
                    approve: 'Changes approved successfully!',
                    reject: 'Changes rejected'
                };
                if (action === 'approve')
                    toast.success(response.data?.message || messages[action]);
                else
                    toast.info(response.data?.message || messages[action])

                // Close modals
                setShowApproveModal(false);
                setShowRejectModal(false);

                onSuccess();
                onClose();
            }
        } catch (error) {
            toast.error(`Failed to ${action} changes`);
        } finally {
            setProcessing(false);
        }
    };

    return (
        <>
            <div className="flex flex-col gap-3">
                {/* Status Info */}
                {productTitle && (
                    <div className="text-sm text-gray-600 mt-3">
                        Reviewing changes for <strong>{productTitle}</strong>
                        {!versionId && (
                            <span className="ml-2 text-xs text-blue-600">
                                (New product submission)
                            </span>
                        )}
                    </div>
                )}

                {/* Action Buttons */}
                <div className="flex flex-col gap-2">
                    <div className="flex gap-2">
                        <button
                            type="button"
                            onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                setShowApproveModal(true)
                            }}
                            disabled={processing}
                            className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 flex items-center justify-center gap-2"
                        >
                            <CheckCircle className="h-4 w-4" />
                            Approve
                        </button>
                        <button
                            type="button"
                            onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                setShowRejectModal(true)
                            }}
                            disabled={processing}
                            className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 flex items-center justify-center gap-2"
                        >
                            <XCircle className="h-4 w-4" />
                            Reject
                        </button>
                    </div>
                </div>
            </div>

            {/* Approve Confirmation Modal */}
            <ConfirmationModal
                isOpen={showApproveModal}
                onClose={() => setShowApproveModal(false)}
                onConfirm={() => handleReviewAction('approve')}
                title="Approve Changes?"
                message={
                    versionId
                        ? `Are you sure you want to approve the changes to "${productTitle}"? This will apply the changes and make the product live.`
                        : `Are you sure you want to approve "${productTitle}"? This will make the product live.`
                }
                confirmText="Approve"
                cancelText="Cancel"
                type="success"
                isProcessing={processing}
            />

            {/* Reject Modal */}
            <RejectModal
                isOpen={showRejectModal}
                onClose={() => setShowRejectModal(false)}
                onConfirm={(action: string, reason: string) => handleReviewAction(action, reason)}
                productTitle={productTitle}
                vendorName={vendorName}
                hasPreviousVersion={hasPreviousVersion}
                isProcessing={processing}
            />
        </>
    );
};

export default ProductReviewActions;